import { useState, useLayoutEffect,
  KeyboardEvent,
  ChangeEvent,
  useEffect,
} from 'react';
import CurrencyInput from 'react-currency-input-field';
import { LiliButton } from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import { RANGE_PICKER_TEXT, STYLES } from './splitConsts';
import { STYLES as generalStyles } from '../../../consts';
import Icon from '../../../../../generic-components/Icon';
import lifeExpensesIcon from '../../../../../assets/icons/svg/colored-icons/ic-colored-categorization-personal.svg';
import workExpenseIcon from '../../../../../assets/icons/svg/colored-icons/ic-colored-categorization-business.svg';
import workExpenseDisabledIcon from '../../../../../assets/icons/svg/colored-icons/ic-colored-categorization-business-disabled.svg';
import lifeExpenseDisabledIcon from '../../../../../assets/icons/svg/colored-icons/ic-colored-categorization-personal-disabled.svg';
import Heading from '../../../../../generic-components/Heading';
import { LiliTransaction } from '../../../types/lili-transaction';

interface IProps {
    isDisabled? :boolean;
    transaction: LiliTransaction;
    onSave: (businessPercent: number) => void;
}

enum INPUT_TYPES {
  BUSINESS = 'BUSINESS',
  PERSONAL = 'PERSONAL',
}

export const SplitTrxModified = ({ isDisabled, transaction, onSave }: IProps) => {
  const [trxAmount, setTrxAmount] = useState(0);
  const [rangeValue, setRangeValue] = useState<number>(0);
  const [businessInputValue, setBusinessInputValue] = useState<string>('');
  const [personalInputValue, setPersonalInputValue] = useState<string>('');
  const { t } = useTranslation();

  function handleTransactionChange() {
    setTrxAmount(Math.abs(transaction.amount));
    const localTrxAmount = Math.abs(transaction.amount);
    if (transaction.categoryDetails?.businessPercent) {
      const numericValue = localTrxAmount * transaction.categoryDetails.businessPercent;
      setPersonalInputValue((localTrxAmount - numericValue).toFixed(2));
      setBusinessInputValue(numericValue.toFixed(2));
      setRangeValue(transaction.categoryDetails.businessPercent * 100);
    } else {
      setBusinessInputValue((localTrxAmount / 2).toFixed(2));
      setPersonalInputValue((localTrxAmount / 2).toFixed(2));
      setRangeValue(50);
    }
  }

  const handleAmountChange = (rawVal: string | undefined, type: INPUT_TYPES) => {
    const val = rawVal?.replace('$', '').replaceAll(',', '');
    if (type === INPUT_TYPES.BUSINESS) {
      if (!val) {
        setBusinessInputValue('0');
      } else if (Number(val) > trxAmount) {
        setBusinessInputValue(trxAmount.toFixed(2));
        setPersonalInputValue('0');
      } else {
        setBusinessInputValue(val);
        setPersonalInputValue((trxAmount - Number(val)).toFixed(2));
      }
      const numericValue = Number(businessInputValue);
      const percentFromTrxAmount = (numericValue / trxAmount) * 100;
      if (percentFromTrxAmount > 100) setRangeValue(100);
      else if (percentFromTrxAmount < 0) setRangeValue(0);
      else setRangeValue(percentFromTrxAmount);
    }
    if (type === INPUT_TYPES.PERSONAL) {
      if (!val) {
        setPersonalInputValue('0');
      } else if (Number(val) > trxAmount) {
        setPersonalInputValue(trxAmount.toFixed(2));
        setBusinessInputValue('0');
      } else {
        setPersonalInputValue(Number(val).toFixed(2));
        setBusinessInputValue((trxAmount - Number(val)).toFixed(2));
      }
      const numericValue = Number(personalInputValue);
      const percentFromTrxAmount = (numericValue / trxAmount) * 100;
      if (percentFromTrxAmount > 100) setRangeValue(0);
      else if (percentFromTrxAmount < 0) setRangeValue(100);
      else setRangeValue(100 - percentFromTrxAmount);
    }
  };

  const onSliderChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    setRangeValue(newValue);
    const personalValue = (newValue * trxAmount) / 100;
    setPersonalInputValue((trxAmount - personalValue).toFixed(2));
    setBusinessInputValue(personalValue.toFixed(2));
  };

  const formatNumber = (num: number) => {
    if (num % 1 !== 0) {
      return num.toFixed(1);
    }
    return num;
  };

  const getPixelsForPercentage = (percentage: number, totalPixels: number = 390) => {
    const ratio = percentage / 100;
    return totalPixels * ratio;
  };

  const handleKeyDown = (e: any, type: INPUT_TYPES) => {
    if (e.code === 'Enter') {
      handleAmountChange(e.target.value, type);
      e.target.blur();
    }
  };

  const handleSaveNClose = () => {
    const numericValue = Number(personalInputValue) || 0;
    const percentFromTrxAmount = (numericValue / trxAmount) * 100;
    onSave(1 - percentFromTrxAmount / 100);
  };

  useLayoutEffect(() => {
    handleTransactionChange();
  }, []);

  useEffect(() => {
    handleTransactionChange();
  }, [transaction.transactionId]);

  return (
    <div className='relative w-full'>
      <label htmlFor='customRange1' className={`${STYLES.RANGE_PICKER.RANGE_PICKER_LABEL} ${isDisabled ? 'opacity-50' : ''}`}>
        {(RANGE_PICKER_TEXT.LABEL)}
      </label>
      <div className='w-full mx-auto my-0 relative'>
        <div style={{ marginLeft: `${getPixelsForPercentage(rangeValue) - getPixelsForPercentage(rangeValue, 30)}px` }} className='w-[30px] h-[30px] z-[-1] rounded-[50%] absolute left-0 top-0'>
          <div
            className={STYLES.RANGE_PICKER.TOOLTIP_WRAPPER}
          >
            <p className={`${STYLES.RANGE_PICKER.TOOLTIP_TEXT} text-left ${isDisabled ? 'opacity-50' : ''}`} data-testid='categorization-split-personal-percent'>{formatNumber(100 - rangeValue)}</p>
            <div className={STYLES.RANGE_PICKER.TOOLTIP_DIVIDER} />
            <p className={`${STYLES.RANGE_PICKER.TOOLTIP_TEXT} text-right ${isDisabled ? 'opacity-50' : ''}`} data-testid='categorization-split-business-percent'>{formatNumber(rangeValue)}</p>

          </div>
        </div>
        <input
          disabled={isDisabled}
          value={rangeValue}
          min={0}
          max={100}
          step={1}
          id='splitTransactionRange'
          className={`w-full h-[6px] ${isDisabled ? 'bg-bw04' : 'bg-[#C5E3FF]'} appearance-none rounded-md cursor-pointer`}
          type='range'
          onChange={onSliderChange}
        />
      </div>

      <div className={`${STYLES.RANGE_PICKER.SPLITTING_RESULT_WRAPPER} ${isDisabled ? 'opacity-50' : ''}`}>

        <div className={STYLES.RANGE_PICKER.SPLITTING_RESULT_BOX}>
          <Icon iconId='lifeExpensesIcon' className={STYLES.RANGE_PICKER.ICON_PERSONAL} src={isDisabled ? lifeExpenseDisabledIcon : lifeExpensesIcon} />
          <div className='flex flex-col'>
            <CurrencyInput
              disabled={isDisabled}
              onKeyDown={(e: KeyboardEvent) => handleKeyDown(e, INPUT_TYPES.PERSONAL)}
              onBlur={(e:any) => handleAmountChange(e.target.value, INPUT_TYPES.PERSONAL)}
              onValueChange={(val) => (val ? setPersonalInputValue(val) : setPersonalInputValue('0'))}
              prefix='$'
              value={personalInputValue}
              decimalsLimit={2}
              className={`${generalStyles.SPLIT_CATEGORY_TAB.CURRENCY_INPUT} text-left`}
            />
            <div className='w-full h-[1px] bg-action03' />
            <Heading title='Personal' className='font-metricR text-[14px] text-bw07 text-left' />
          </div>
        </div>

        <div className={STYLES.RANGE_PICKER.SPLITTING_RESULT_BOX}>
          <div className='flex flex-col'>
            <CurrencyInput
              disabled={isDisabled}
              onKeyDown={(e: KeyboardEvent) => handleKeyDown(e, INPUT_TYPES.BUSINESS)}
              onBlur={(e:any) => handleAmountChange(e.target.value, INPUT_TYPES.BUSINESS)}
              onValueChange={(val) => (val ? setBusinessInputValue(val) : setBusinessInputValue('0'))}
              prefix='$'
              value={businessInputValue}
              decimalsLimit={2}
              className={`${generalStyles.SPLIT_CATEGORY_TAB.CURRENCY_INPUT} text-right`}
            />
            <div className='w-full h-[1px] bg-action03' />
            <Heading title='Business' className='font-metricR text-[14px] text-bw07 text-right' />
          </div>
          <Icon iconId='workExpenseIcon' className={STYLES.RANGE_PICKER.ICON_BUSINESS} src={isDisabled ? workExpenseDisabledIcon : workExpenseIcon} />
        </div>
      </div>
      <div className='mt-[16px] border-t border-bw03'>
        <LiliButton
          disabled={isDisabled}
          className='w-full mt-[16px]'
          id='saveNclose'
          title={t('SAVE_AND_CLOSE')}
          onClick={handleSaveNClose}
        />
      </div>
    </div>
  );
};
